import { useEffect, useState, useRef } from "react";
import Input from "../form/Input";

const LocationSearch = ({ onLocationSelect = () => {}, value = "" }) => {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (query.length < 3) {
      setResults([]);
      return;
    }

    const controller = new AbortController();
    const delayDebounce = setTimeout(() => {
      fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${query}`,
        { signal: controller.signal }
      )
        .then((res) => res.json())
        .then((data) => setResults(data))
        .catch((err) => {
          if (err.name !== "AbortError") console.error(err);
        });
    }, 300);

    return () => {
      clearTimeout(delayDebounce);
      controller.abort();
    };
  }, [query]);

  useEffect(() => {
    // Update internal state when `value` prop changes (e.g. on form load)
    setQuery(value);
  }, [value]);

  // Handle clicks outside the component
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsFocused(false);
        setIsSelecting(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (place) => {
    setIsSelecting(true);
    setQuery(place.display_name);
    setResults([]);
    setIsFocused(false);

    if (onLocationSelect) {
      onLocationSelect({
        ...place,
      });
    }

    // Reset selecting state after a short delay
    setTimeout(() => {
      setIsSelecting(false);
    }, 100);
  };

  const handleInputChange = (e) => {
    if (!isSelecting) {
      setQuery(e.target.value);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const handleInputBlur = (e) => {
    // Don't hide dropdown if clicking on a result item
    if (!containerRef.current?.contains(e.relatedTarget)) {
      setTimeout(() => {
        if (!isSelecting) {
          setIsFocused(false);
        }
      }, 150);
    }
  };

  const handleMouseDown = (e) => {
    // Prevent input blur when clicking on dropdown items
    e.preventDefault();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsFocused(false);
      setResults([]);
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <Input
        ref={inputRef}
        inputType="text"
        inputPlaceholder="Search location"
        value={query}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />
      {isFocused && results.length > 0 && (
        <ul
          onMouseDown={handleMouseDown}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "#fff",
            listStyle: "none",
            margin: 0,
            padding: "0.5rem",
            border: "1px solid #ccc",
            borderTop: "none",
            maxHeight: "200px",
            overflowY: "auto",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            borderRadius: "0 0 4px 4px",
          }}
        >
          {results.map((place, idx) => (
            <li
              key={idx}
              onClick={() => handleSelect(place)}
              onMouseDown={(e) => e.preventDefault()}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                borderBottom:
                  idx < results.length - 1 ? "1px solid #eee" : "none",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
              }}
            >
              {place.display_name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LocationSearch;
